import { LoadCostCentersAction, UserState } from '@celerofinancas/state-management';
import { Store } from '@ngxs/store';

/**
 * Sentinel value passed as `company_pk` when the reports routes need to wake
 * the core-registry interceptor without already knowing the active company.
 *
 * The interceptor's `validateRequest` step substitutes `company_pk` equal to
 * the literal string `'undefined'` with the active company pk once the
 * bootstrap chain has resolved, so this value reaches the backend as the real
 * pk.
 */
const REPORTS_BOOTSTRAP_SENTINEL = 'undefined';

/**
 * Triggers the core-registry interceptor bootstrap from the reports routes.
 *
 * The `/results/monthly` and `/results/comparative` pages do not fire any
 * HTTP request on mount, so the core-registry interceptor never wakes up and
 * the cost center filter stays empty until the user submits an analysis.
 *
 * This helper dispatches `LoadCostCentersAction` with the sentinel
 * `'undefined'` as `company_pk` to force a request, which:
 *
 *   1. Wakes the interceptor and runs its full bootstrap chain (user,
 *      economic group, company, accounts, keychain).
 *   2. Gets re-fired by the interceptor with the real `company_pk` value via
 *      its `validateRequest` substitution.
 *   3. Marks `CostCenterState.loading` as `true` so the interceptor's own
 *      `LoadCostCentersAction` dispatched inside the bootstrap batch
 *      short-circuits and the cost center endpoint is hit exactly once.
 *
 * Using the cost center action as the trigger avoids the `me/read`
 * duplication that would happen if the CL dispatched `LoadUserAction`
 * directly, because the interceptor itself dispatches `LoadUserAction` inside
 * its bootstrap pipeline.
 *
 * Scope: pages that do NOT fire any HTTP request on mount and therefore never
 * wake the interceptor on their own. Today that means the reports routes
 * (/results/monthly, /results/comparative) and the initial dashboard
 * (dashboard-finance) — the latter renders bfm-dashboard-summary from
 * ui-reports (>= 2.3.x), which defers every request until
 * CompanyState.getActiveCompanyPk emits, so without this trigger the company
 * never hydrates and the dashboard stays empty. Do not call it from pages that
 * already fire HTTP on mount (they wake the interceptor naturally).
 *
 * @param store - NGXS store used to read the user state and dispatch the
 *   trigger action.
 */
export function triggerReportsBootstrapWarmup(store: Store): void {
  if (store.selectSnapshot(UserState.getState)?.pk) {
    return;
  }

  store.dispatch(
    new LoadCostCentersAction({ company_pk: REPORTS_BOOTSTRAP_SENTINEL }),
  );
}
